import { Box, Button, DialogContent, Typography, styled, useMediaQuery, IconButton } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import { useState, useMemo } from 'react'
import theme from '../../../../../config/theme'
import Payment from '../../modals/PaymentModal'

export const StyledButton = styled(Button)(() => ({
  background: '#202CD3',
  borderRadius: '75px',
  border: 'none',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  padding: '10px 30px',
  '&:hover': {
    background: '#1A23B8',
    border: 'none'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem'
  }
})) as typeof Button

export const SecondaryButton = styled(Button)(() => ({
  background: 'transparent',
  borderRadius: '75px',
  border: 'none',
  color: '#000',
  textTransform: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  padding: '10px 30px',
  '&:hover': {
    background: 'transparent',
    border: 'none'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem'
  }
})) as typeof Button

interface DowngradeFreePlanModalProps {
  isOpen: boolean
  onClose: () => void
  planCycle: string
}

export default function DowngradeFreePlanModal({ isOpen, onClose, planCycle }: Readonly<DowngradeFreePlanModalProps>) {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [planName, setPlanName] = useState('')
  const [planPrice, setPlanPrice] = useState('')

  // Memoize the formatted date to avoid creating new Date() on every render
  const formattedDate = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }, [])

  const handleContinueWithStorage = () => {
    setPlanName('storage')
    setPlanPrice(planCycle === 'annual' ? '50.00' : '5.00')
    setShowPaymentModal(true)
    onClose()
  }

  const handleUpgradeNow = () => {
    setPlanName('sharing')
    setPlanPrice(planCycle === 'annual' ? '80.00' : '8.00')
    setShowPaymentModal(true)
    onClose()
  }

  const handlePaymentClose = () => {
    setShowPaymentModal(false)
  }

  return (
    <>
      <Dialog data-test="downgrade-free-plan-modal" open={isOpen} onClose={onClose} maxWidth="sm">
        <DialogTitle sx={{ textAlign: 'left', paddingBottom: '8px', position: 'relative' }}>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (icontheme) => icontheme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ fontWeight: '600', fontSize: '24px', color: '#000' }}>
            Heads up! The{' '}
            <Typography component="span" sx={{ color: '#202CD3', fontWeight: '600', fontSize: '24px' }}>
              Storage Plan
            </Typography>{' '}
            limits sharing.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px', textAlign: 'left' }}>
          <Typography sx={{ marginBottom: '16px', fontSize: '14px', lineHeight: '20px', color: '#000' }}>
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              This plan is great for storage and backup, but it&apos;s not built for sharing. Here&apos;s what happens when you switch to the Storage
              Plan:
            </Typography>
          </Typography>

          <Box sx={{ marginBottom: '16px' }}>
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: '#000', marginBottom: '8px' }}>
              • Any active invite links will be disabled.
            </Typography>
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: '#000', marginBottom: '8px' }}>
              • You will no longer be able to invite any new members.
            </Typography>
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: '#000', marginBottom: '8px' }}>
              • Existing members in your Bucket(s) will stay, but collaboration features will be limited.
            </Typography>
            <Typography sx={{ fontSize: '14px', lineHeight: '20px', color: '#000', marginBottom: '16px' }}>
              • Other key features such as image editing & video playback will no longer be available.
            </Typography>
          </Box>

          <Typography sx={{ marginBottom: '24px', fontSize: '14px', lineHeight: '20px', color: '#000' }}>
            Want to keep sharing and collaborating without restrictions? Upgrade to the{' '}
            <Typography component="span" sx={{ color: '#800080', fontWeight: '600' }}>
              Group Buckets Plan
            </Typography>{' '}
            for unlimited buckets, full collaboration, and more!
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: isMobile ? '8px' : '16px' }}>
            <SecondaryButton
              sx={{ minWidth: '140px', fontWeight: '600', padding: '8px 16px', fontSize: isMobile ? '0.7rem' : '0.875rem' }}
              onClick={handleContinueWithStorage}
            >
              Continue with Storage
            </SecondaryButton>
            <StyledButton
              sx={{ minWidth: '120px', fontWeight: '600', padding: '8px 16px', fontSize: isMobile ? '0.7rem' : '0.875rem' }}
              onClick={handleUpgradeNow}
            >
              Upgrade now!
            </StyledButton>
          </Box>
        </DialogContent>
      </Dialog>
      <Payment
        open={showPaymentModal}
        onClose={handlePaymentClose}
        planName={planName}
        billingCycle={planCycle}
        total={planPrice}
        buttonText="Complete Purchase"
        content={`Your first payment of $${planPrice} will be on ${formattedDate} and billing will recur on a ${planCycle} basis. You can cancel anytime.`}
      />
    </>
  )
}
